import { inject, Injectable, NgZone } from '@angular/core';
import { UserPermissions } from '@grid-ui/api-models';
import { FeatureFlagService } from '@grid-ui/feature-flag';
import { WhoAmIService } from '@grid-ui/whoami';
import { firstValueFrom } from 'rxjs';
import { PermissionsPollingService } from './permissions-polling.service';

export class PermissionSelector {
  public static readonly COMMODITY_RISK = {
    ANALYSE: new PermissionSelector({ module: 'always', area: 'deny', routingDeniedRedirect: '/home' }),
    HOME: new PermissionSelector({ module: 'always', area: 'deny', routingDeniedRedirect: '/commodity-risk/dashboard' }),
    INSIGHTS: new PermissionSelector({ module: 'commodity-risk', area: 'insights', routingDeniedRedirect: '/home' }),
    DASHBOARD: new PermissionSelector({ module: 'commodity-risk', area: 'dashboard', routingDeniedRedirect: '/commodity-risk/insights' }),
    SNAPSHOTS: new PermissionSelector({ module: 'always', area: 'deny', routingDeniedRedirect: '/home' }),
    VIEWS: new PermissionSelector({ module: 'always', area: 'deny', routingDeniedRedirect: '/home' }),
    MODULE: new PermissionSelector({ module: 'commodity-risk', area: 'module', routingDeniedRedirect: '/home' }),
  };

  public static readonly COUNTRY_RISK = {
    ANALYSE: new PermissionSelector({ module: 'country-risk', area: 'analyse', routingDeniedRedirect: '/country-risk/insights' }),
    HOME: new PermissionSelector({ module: 'country-risk', area: 'home', routingDeniedRedirect: '/country-risk/insights' }),
    INSIGHTS: new PermissionSelector({ module: 'country-risk', area: 'insights', routingDeniedRedirect: '/country-risk/snapshots' }),
    SNAPSHOTS: new PermissionSelector({ module: 'country-risk', area: 'snapshots', routingDeniedRedirect: '/home' }),
    VIEWS: new PermissionSelector({ module: 'country-risk', area: 'views', routingDeniedRedirect: '/country-risk/insights' }),
    MODULE: new PermissionSelector({ module: 'country-risk', area: 'module', routingDeniedRedirect: '/home' }),
    ALERTS: new PermissionSelector({ module: 'country-risk', area: 'alerts', routingDeniedRedirect: '/country-risk' }),
    DEEP_DIVE: new PermissionSelector({ module: 'country-risk', area: 'deep_dive', routingDeniedRedirect: '/country-risk/deep-dive' }),
    DEEP_DIVE_PROMO: new PermissionSelector({ module: 'country-risk', area: 'deep_dive_promo', routingDeniedRedirect: '/country-risk' }),
    DEEP_DIVE_LINKS: new PermissionSelector({ module: 'country-risk', area: 'deep_dive_links', routingDeniedRedirect: '/country-risk' }),
    DEEP_DIVE_BADGE: new PermissionSelector({ module: 'country-risk', area: 'deep_dive_badge', routingDeniedRedirect: '/country-risk' }),
  };

  public static readonly INDUSTRY = {
    MODULE: new PermissionSelector({ module: 'industry', area: 'module', routingDeniedRedirect: '/home' }),
    BEST_WORST: new PermissionSelector({ module: 'industry', area: 'bestWorst', routingDeniedRedirect: '/industry' }),
  };

  public static readonly DATA_WIZARD = {
    EXTRACT: new PermissionSelector({ module: 'data-wizard', area: 'extract', routingDeniedRedirect: '/data-wizard/upload' }),
    UPLOAD: new PermissionSelector({ module: 'data-wizard', area: 'upload', routingDeniedRedirect: '/home' }),
    MODULE: new PermissionSelector({ module: 'data-wizard', area: 'module', routingDeniedRedirect: '/home' }),
  };

  public static readonly DOCUMENT_LIBRARY = {
    EXTRACTS: new PermissionSelector({ module: 'document-library', area: 'module', routingDeniedRedirect: '/document-library' }),
    MODULE: new PermissionSelector({ module: 'document-library', area: 'module', routingDeniedRedirect: '/home' }),
  };

  public static readonly API_DOCS = {
    LINK: new PermissionSelector({ module: 'api-docs', area: 'link' }),
  };

  public static readonly API_DOCS_INDUSTRY = {
    LINK: new PermissionSelector({ module: 'api-docs-industry', area: 'link' }),
  };

  public static readonly SUBSCRIPTIONS = {
    COUNTRY_RISK: new PermissionSelector({ module: 'subscriptions', area: 'country-risk', routingDeniedRedirect: '/home' }),
    INDUSTRY_RISK: new PermissionSelector({ module: 'subscriptions', area: 'industry-risk', routingDeniedRedirect: '/home' }),
    COMMODITY_RISK: new PermissionSelector({ module: 'always', area: 'deny', routingDeniedRedirect: '/subscriptions/details' }),
    DATA_WIZARD: new PermissionSelector({ module: 'always', area: 'deny', routingDeniedRedirect: '/subscriptions/details' }),
    MODULE: new PermissionSelector({ module: 'subscriptions', area: 'module', routingDeniedRedirect: '/home' }),
  };

  public static readonly FAQ_LEARN = {
    ABOUT_MAPLECROFT: new PermissionSelector({ module: 'always', area: 'allow' }),
    COUNTRY_RISK: new PermissionSelector({ module: 'country-risk', area: 'module', routingDeniedRedirect: '/learn' }),
    COMMODITY_RISK: new PermissionSelector({ module: 'commodity-risk', area: 'module', routingDeniedRedirect: '/learn' }),
    INDUSTRY_RISK: new PermissionSelector({ module: 'industry', area: 'module', routingDeniedRedirect: '/learn' }),
    DOCUMENT_LIBRARY: new PermissionSelector({ module: 'document-library', area: 'module', routingDeniedRedirect: '/learn' }),
    DATA_WIZARD: new PermissionSelector({ module: 'data-wizard', area: 'module', routingDeniedRedirect: '/learn' }),
    EMAIL_ALERTS: new PermissionSelector({ module: 'always', area: 'deny' }),
    MODULE: new PermissionSelector({ module: 'always', area: 'allow' }),
  };

  public static readonly API_TOKENS = {
    MODULE: new PermissionSelector({ module: 'api-tokens', area: 'module', routingDeniedRedirect: '/home' }),
    IS_API_TOKEN_CREATOR: new PermissionSelector({
      module: 'api-tokens',
      area: 'isApiTokenCreator',
      routingDeniedRedirect: '/api-token/org-tokens',
    }),
    IS_API_ACCOUNT_ADMIN: new PermissionSelector({
      module: 'api-tokens',
      area: 'isAccountAdmin',
      routingDeniedRedirect: '/api-token/my-tokens',
    }),
  };

  public static readonly ALWAYS = {
    ALLOW: new PermissionSelector({ module: 'always', area: 'allow' }),
    DENY: new PermissionSelector({ module: 'always', area: 'deny' }),
  };

  public static readonly GRID = {
    ACCOUNT_SETTINGS: new PermissionSelector({ module: 'grid', area: 'account_settings' }),
  };

  public readonly module: string;
  public readonly area: string;
  public readonly routingDeniedRedirect?: string;

  // private - we only want the static instances, do not allow new instances to be created
  private constructor({ module, area, routingDeniedRedirect }: { module: string; area: string; routingDeniedRedirect?: string }) {
    this.module = module;
    this.area = area;
    this.routingDeniedRedirect = routingDeniedRedirect;
  }
}

@Injectable()
export class PermissionsService {
  private readonly whoAmIService = inject(WhoAmIService);
  private readonly permissionsPollingService = inject(PermissionsPollingService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly ngZone = inject(NgZone);
  private readonly whoAmI = firstValueFrom(this.whoAmIService.getWhoAmI());

  public userPermissions = firstValueFrom(this.whoAmIService.getPermissions({ forceAPICall: true }));

  public permissions: { [key: string]: { [key: string]: () => Promise<boolean> } } = {
    always: {
      allow: () => Promise.resolve(true),
      deny: () => Promise.resolve(false),
    },
    'commodity-risk': {
      dashboard: this.userHasCommodityRiskArrayPopulated.call(this, 'commodities'),
      insights: this.userHasCommodityRiskArrayPopulated.call(this, 'insights'),
      module: this.userHasCommodityRiskArrayPopulated.call(this, 'commodities'),
    },
    'country-risk': {
      analyse: this.userHasAnalyseAccess.bind(this),
      home: this.userHascountryRiskArrayPopulated.call(this, 'index'),
      insights: this.userHascountryRiskArrayPopulated.call(this, 'insights'),
      snapshots: this.userHascountryRiskArrayPopulated.call(this, 'snapshots'),
      views: this.userHascountryRiskArrayPopulated.call(this, 'index'),
      module: () => this.userHasCountryRiskAccess(),
      alerts: this.userHasCountryAlertsAccess.bind(this),
      deep_dive: this.userHasDeepDiveAccess.bind(this),
      deep_dive_promo: this.userHasDeepDivePromoAccess.bind(this),
      deep_dive_links: this.showDeepDiveLinks.bind(this),
      deep_dive_badge: this.showDeepDiveBadge.bind(this),
    },
    'data-wizard': {
      extract: this.userHasDataWizardExtractAccess.bind(this),
      module: () =>
        Promise.all([this.userHasDataWizardExtractAccess.bind(this)(), this.userHascountryRiskArrayPopulated.call(this, 'index')()]).then(
          (permission) => permission.includes(true),
        ),
      upload: this.userHascountryRiskArrayPopulated.call(this, 'index'),
    },
    'document-library': {
      module: () => firstValueFrom(this.whoAmIService.getPermissions()).then((permission) => !!permission['document-library']),
    },
    industry: {
      module: () => this.userHasIndustryRiskPopulated().call(this),
      bestWorst: () => Promise.resolve(!!this.featureFlagService.getFlagValue('industryBestWorst')),
    },
    'api-docs': {
      link: this.userHasApiDocsAccess.bind(this),
    },
    'api-docs-industry': {
      link: this.userHasApiDocsIndustryAccess.bind(this),
    },
    'api-tokens': {
      module: () => this.userHasApiTokensAccess(),
      isApiTokenCreator: () => this.userIsTokenCreator(),
      isAccountAdmin: () => this.userIsAccountAdmin(),
    },
    subscriptions: {
      'country-risk': () => this.userHasCountryRiskAccess(),
      'industry-risk': () => this.userHasIndustryRiskPopulated().call(this),
      module: () =>
        Promise.all([this.userHasCountryRiskAccess(), this.userHasIndustryRiskPopulated().call(this)]).then((permissions) =>
          permissions.includes(true),
        ),
    },
    grid: {
      account_settings: () => this.userHasAccountSettingsAccess(),
    },
  };

  constructor() {
    this.ngZone.runOutsideAngular(() => {
      this.permissionsPollingService.startPolling().subscribe((permissions) => {
        this.userPermissions = Promise.resolve(permissions.permissions);
      });
    });
  }

  public isAllowed(permission: PermissionSelector): Promise<boolean> {
    return this.permissions[permission.module]?.[permission.area]?.() ?? Promise.resolve(false);
  }

  private userHasIndustryRiskPopulated(): () => Promise<boolean> {
    return () =>
      this.userPermissions.then(
        (userPermission: UserPermissions) =>
          !!this.featureFlagService.getFlagValue('industry') &&
          !!userPermission['industry-risk'] &&
          !!userPermission['industry-risk']['index'] &&
          !!userPermission['industry-risk']['index'].length &&
          !!userPermission['industry-risk']['industry'] &&
          !!userPermission['industry-risk']['industry'].length,
      );
  }

  private userHasAnalyseAccess(): Promise<boolean> {
    return Promise.all([
      this.userHascountryRiskArrayPopulated.call(this, 'index')(),
      this.userHascountryRiskArrayPopulated.call(this, 'countries')(),
    ]).then((permissions) => permissions.every((b) => b));
  }

  private userHascountryRiskArrayPopulated(name: 'index' | 'snapshots' | 'insights' | 'countries'): () => Promise<boolean> {
    return () =>
      this.userPermissions.then((permission) => {
        if (name === 'snapshots') {
          return !!(permission['country-risk'] && permission['country-risk']['cms_snapshot']?.length);
        }
        if (name === 'insights') {
          return !!(
            permission['country-risk'] &&
            (permission['country-risk']['cms_insight']['country']?.length ||
              permission['country-risk']['cms_insight']['region']?.length ||
              permission['country-risk']['cms_insight']['theme']?.length)
          );
        }
        return !!(permission['country-risk'] && permission['country-risk'][name] && permission['country-risk'][name].length);
      });
  }

  private userHasCommodityRiskArrayPopulated(name: 'commodities' | 'insights'): () => Promise<boolean> {
    return () =>
      this.userPermissions.then((permission) => {
        if (name === 'insights') {
          return !!(
            permission['commodity-risk']?.length &&
            (permission['country-risk']?.['cms_insight']['country']?.length ||
              permission['country-risk']?.['cms_insight']['region']?.length ||
              permission['country-risk']?.['cms_insight']['theme']?.length)
          );
        }
        return !!permission['commodity-risk']?.length;
      });
  }

  private userHasDataWizardExtractAccess(): Promise<boolean> {
    // TODO: remove boolean check once all environments are returning an object for this attribute
    return this.userPermissions.then((permission) => {
      if (!permission['data-wizard']) {
        return false;
      }
      if (typeof permission['data-wizard'] === 'boolean') {
        return permission['data-wizard'];
      }
      return !!permission['data-wizard'].extract;
    });
  }

  private userHasApiDocsAccess(): Promise<boolean> {
    return this.userPermissions.then((permission) => !!permission['country-risk']?.documentation);
  }

  private userHasApiDocsIndustryAccess(): Promise<boolean> {
    return this.userPermissions.then((permission) => permission['industry-risk']?.documentation);
  }

  private userHasCountryAlertsAccess(): Promise<boolean> {
    return this.userPermissions.then((permission) => !!(permission['country-risk'] || {})['data-alerts']);
  }

  private async userHasDeepDiveAccess(): Promise<boolean> {
    return this.featureFlagService.getFlagValue('release:deep-dive') && (await this.userHasDeepDiveEntitled());
  }

  private async userHasDeepDivePromoAccess(): Promise<boolean> {
    return this.featureFlagService.getFlagValue('release:deep-dive-promo') && !(await this.userHasDeepDiveEntitled());
  }

  private async userHasDeepDiveEntitled(): Promise<boolean> {
    const permissions = await this.userPermissions;

    return permissions['country-risk']?.['deep_dive'] ?? false;
  }

  private async showDeepDiveLinks(): Promise<boolean> {
    return (await this.userHasDeepDiveAccess()) || (await this.userHasDeepDivePromoAccess());
  }

  private async showDeepDiveBadge(): Promise<boolean> {
    return await this.featureFlagService.getFlagValue('enable:deep-dive-new-badge');
  }

  private userHasCountryRiskAccess(): Promise<boolean> {
    return Promise.all([
      this.userHascountryRiskArrayPopulated.call(this, 'index')(),
      this.userHascountryRiskArrayPopulated.call(this, 'insights')(),
      this.userHascountryRiskArrayPopulated.call(this, 'snapshots')(),
    ]).then((permissions) => permissions.includes(true));
  }

  private userHasAccountSettingsAccess(): Promise<boolean> {
    return this.userPermissions.then((permission) => permission.grid?.account_settings ?? true);
  }

  private userHasApiTokensAccess(): Promise<boolean> {
    return this.whoAmI.then((whoAmI) => (whoAmI?.is_account_admin ?? false) || (whoAmI?.is_token_creator ?? false));
  }

  private userIsTokenCreator(): Promise<boolean> {
    return this.whoAmI.then(
      (whoAmI) =>
        // Account admins can access my-tokens route (even if not token creators)
        // OR users who are token creators
        (whoAmI?.is_account_admin ?? false) || (whoAmI?.is_token_creator ?? false),
    );
  }

  private userIsAccountAdmin(): Promise<boolean> {
    return this.whoAmI.then((whoAmI) => whoAmI?.is_account_admin ?? false);
  }
}
